/**
 * Human-readable causes for browser softphone failures.
 *
 * These exist because the Twilio Voice SDK reports call failures somewhere our
 * code was not looking. `Device.connect()` fires `Call.accept()` *without*
 * awaiting it (voice-sdk/esm/twilio/device.js:581) and returns, so the
 * microphone is opened after `connect()` has already resolved — a try/catch
 * around `connect()` can never see it. `Call.accept()` then calls
 * `_disconnect()`, logs at `debug` level only, and emits `error` on the Call
 * (call.js:899-901). With no `error` listener and `logLevel: "warn"`, that
 * produced a call panel that vanished with a clean console.
 *
 * Codes below are read from the installed SDK (`errors/generated.js`), not
 * guessed: the SDK emits 31401/31402 for user-media problems, while 31208/31201
 * are the underlying media-layer codes `Call.accept()` matches on (call.js:880).
 * Both are mapped.
 */

const MIC_BLOCKED =
  'Microphone access is blocked. Click the padlock in your address bar, allow the microphone, then try again.';
const MIC_UNAVAILABLE =
  'No working microphone was found. Check that one is plugged in and not in use by another app.';
const MIC_IN_USE =
  'Your microphone is being used by another app. Close it (Zoom, Teams, Meet) and try again.';
const NETWORK_BLOCKED =
  'Your network is blocking call audio. Try a different network, or ask IT to allow Twilio voice traffic.';
const CONNECTION_DROPPED = 'The call connection dropped. Please try again.';
const UNKNOWN = 'The call could not be completed. Please try again.';

/** Twilio error codes → cause. Anything unlisted falls back to the SDK message. */
const BY_CODE: Record<number, string> = {
  31000: CONNECTION_DROPPED, // GeneralErrors.ConnectionError
  31003: NETWORK_BLOCKED, // ICE liveliness check failed / connectivity lost
  31005: CONNECTION_DROPPED, // ConnectionError — transport dropped
  31201: MIC_UNAVAILABLE, // media-layer acquisition failure
  31208: MIC_BLOCKED, // media-layer permission denial (call.js:880)
  31401: MIC_BLOCKED, // UserMediaErrors.PermissionDeniedError
  31402: MIC_UNAVAILABLE, // UserMediaErrors.AcquisitionFailedError
  53000: CONNECTION_DROPPED, // SignalingErrors.ConnectionError
  53405: NETWORK_BLOCKED, // MediaErrors.ConnectionError — media never established
};

/** Twilio error class names, for the paths that arrive without a numeric code. */
const BY_NAME: Record<string, string> = {
  AcquisitionFailedError: MIC_UNAVAILABLE,
  NotAllowedError: MIC_BLOCKED,
  NotFoundError: MIC_UNAVAILABLE,
  NotReadableError: MIC_IN_USE,
  PermissionDeniedError: MIC_BLOCKED,
  SecurityError: MIC_BLOCKED,
};

/** Our own API's refusals of a call, by the `code` an ApiRequestError carries. */
const BY_API_CODE: Record<string, string> = {
  CALLER_ID_NOT_AVAILABLE: "That number can't be used for calls. Pick another number and call again.",
  CALLER_ID_CHECK_FAILED: "We couldn't check the number you chose to call from. Try the call again in a moment.",
};

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Describe a `getUserMedia()` rejection from our own pre-flight check.
 *
 * Browsers signal the cause through `DOMException.name`, not a code, and the
 * distinction matters to the user: a blocked permission is fixed in the address
 * bar, a missing device is not.
 */
export function describeMediaError(err: unknown): string {
  if (err && typeof err === 'object') {
    const name = readString(err as Record<string, unknown>, 'name');
    if (name && BY_NAME[name]) return BY_NAME[name];
    // OverconstrainedError names the constraint rather than the failure.
    if (name === 'OverconstrainedError') return MIC_UNAVAILABLE;
  }

  return `${MIC_UNAVAILABLE} If the problem persists, try a different browser.`;
}

/**
 * Describe a Twilio `Device`/`Call` error. Falls back to the SDK's own message
 * so an unmapped code still says something specific rather than nothing.
 */
export function describeVoiceError(err: unknown): string {
  if (typeof err === 'string' && err.trim() !== '') return err.trim();

  if (err && typeof err === 'object') {
    const source = err as Record<string, unknown>;

    const code = source.code;
    if (typeof code === 'number' && BY_CODE[code]) return BY_CODE[code];
    if (typeof code === 'string' && BY_API_CODE[code]) return BY_API_CODE[code];

    const name = readString(source, 'name');
    if (name && BY_NAME[name]) return BY_NAME[name];

    const message = readString(source, 'message');
    if (message) return message;
  }

  return UNKNOWN;
}
